"use client";

import { useEffect, useLayoutEffect, useState } from "react";
import { cn } from "@marketing-sites/utils/cn";
import { Dialog } from "@base-ui/react/dialog";
import { HamburgerIcon } from "../../icons/hamburger-icon";
import { CloseIcon } from "../../icons/close-icon";
import { useHeaderContext } from "../../context";
import { DialogNavItem } from "./dialog-nav-item";
import { MobileNavItem } from "./mobile-nav-item";
import { Logo } from "../logo";
import { navDivider } from "../floating-nav-classes";
import { HeaderLink } from "../header-link";
import { Text } from "../text";
import { floatingDrawerHidden, headerId } from "../../constants";
import { messengerBannerHeightVar } from "../messenger-banner-offset";
import type { MobileMenuNavItem } from "../../types";

export function MobileNav({
  className,
  logoMetadata,
  crossSiteMetadata,
}: {
  className?: string;
  logoMetadata: { analyticsId: string };
  crossSiteMetadata: { analyticsId: string };
}) {
  const {
    fixtureData,
    mobileMenuFixtureData,
    disableMobileMenu,
    disableLogoLink,
    variant,
    desktopBreakpoint,
    labels,
    translatedLabels,
  } = useHeaderContext();
  const [open, setOpen] = useState(false);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleNavItemClick = () => {
      setOpen(false);
      setOpenItems(new Set());
    };
    window.addEventListener("navItemClick", handleNavItemClick);
    return () => window.removeEventListener("navItemClick", handleNavItemClick);
  }, []);

  const [headerHeight, setHeaderHeight] = useState(57);

  useLayoutEffect(() => {
    if (!open) return;
    const header = document.getElementById(headerId);
    if (header) {
      setHeaderHeight(header.clientHeight);
    }
  }, [open]);
  const drawerTop = `calc(${headerHeight}px + var(${messengerBannerHeightVar}, 0px))`;

  // Floating variant: portal the drawer inside the header (which carries the
  // brand font/token scope) instead of the document body, so its content keeps
  // the brand typeface. Default variant portals to body as before.
  const floating = variant === "floating";

  // Below sm, translated CTA labels push this link and the hamburger off the
  // right edge of the top bar, so opted-in locales move it into the drawer —
  // unless there is no drawer, in which case the bar has to keep it.
  const barCrossSite = floating
    ? fixtureData.crossSiteLink
    : mobileMenuFixtureData.crossSiteLink;
  const crossSiteItem = fixtureData.crossSiteLink;
  const drawerCrossSite: MobileMenuNavItem | undefined =
    translatedLabels &&
    !disableMobileMenu &&
    !disableLogoLink &&
    barCrossSite &&
    crossSiteItem?.type === "button"
      ? {
          type: "smallLink",
          label: crossSiteItem.label,
          href: crossSiteItem.href,
          analyticsText: crossSiteItem.analyticsText,
          // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
          analyticsId: `${crossSiteItem.analyticsId}_dialog`,
          isExternal: true,
        }
      : undefined;

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!floating) return;
    setPortalContainer(document.getElementById(headerId));
  }, [floating]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setOpenItems(new Set());
      }}
    >
      {/* TOP BAR */}
      <div className={cn("flex items-center justify-between gap-2", className)}>
        <div className="flex items-center gap-3">
          <Logo
            analyticsId={logoMetadata.analyticsId}
            asLink={!disableLogoLink}
            icon={fixtureData.logo.icon}
            href={fixtureData.logo.href}
            label={fixtureData.logo.label}
            analyticsText={fixtureData.logo.analyticsText}
          />
          {variant !== "floating" &&
            !disableLogoLink &&
            mobileMenuFixtureData.crossSiteLink && (
              <div
                className={cn(
                  "border-nav-border/30 border-l pl-1",
                  drawerCrossSite && "max-sm:hidden",
                )}
              >
                <HeaderLink
                  analyticsId={crossSiteMetadata.analyticsId}
                  href={mobileMenuFixtureData.crossSiteLink.href}
                  text={mobileMenuFixtureData.crossSiteLink.label}
                  analyticsText={
                    mobileMenuFixtureData.crossSiteLink.analyticsText
                  }
                  external={true}
                  externalIconVisible={true}
                  className="hover:bg-nav-hover flex h-10 items-center gap-1 rounded-md p-2"
                >
                  <Text variant="btnLg">
                    {mobileMenuFixtureData.crossSiteLink.label}
                  </Text>
                </HeaderLink>
              </div>
            )}
        </div>

        <ul className="ml-auto flex items-center">
          {fixtureData.secondary.map((item, index) => (
            <li
              // Not the label: this list keeps the mobileOnly duplicates desktop filters out.
              key={index}
              className={cn(
                item.type === "button" && item.mobileOnly
                  ? disableMobileMenu
                    ? "hidden"
                    : "min-[700px]:hidden"
                  : !disableMobileMenu && "hidden last:block min-[700px]:block",
              )}
              data-testid="nav-cta"
            >
              <MobileNavItem item={item} />
            </li>
          ))}
        </ul>

        {variant === "floating" &&
          !disableLogoLink &&
          fixtureData.crossSiteLink && (
            <div
              className={cn(
                "flex items-center gap-[9px]",
                drawerCrossSite && "max-sm:hidden",
              )}
            >
              <div className={cn(navDivider, "h-[33px] w-px")} />
              <ul className="flex items-center">
                <MobileNavItem item={fixtureData.crossSiteLink} />
              </ul>
            </div>
          )}

        <Dialog.Trigger
          className={cn(
            "hover:bg-nav-background-highlight rounded-md p-2",
            disableMobileMenu && "hidden",
          )}
          aria-label={
            open ? labels.closeNavigationMenu : labels.openNavigationMenu
          }
        >
          {open ? <CloseIcon /> : <HamburgerIcon />}
        </Dialog.Trigger>
      </div>

      {/* NAV DRAWER */}
      <Dialog.Portal container={floating ? portalContainer : undefined}>
        <Dialog.Popup
          className={cn(
            "shared-header-portal",
            variant === "floating"
              ? floatingDrawerHidden[desktopBreakpoint]
              : "min-[1140px]:hidden",
            variant === "floating"
              ? "bg-[var(--nav-bar-bg,var(--color-off-white))] text-[var(--nav-fg,var(--color-off-black))]"
              : "bg-nav-background text-nav-content-primary",
            "fixed w-full overflow-y-auto",
            // Default variant keeps its fixed offsets; the floating drawer pins
            // top to the measured header height and bottom to 0 (below) so it
            // always reaches the true viewport bottom — deriving height from
            // 100dvh leaves a gap while the mobile toolbar animates.
            variant !== "floating" &&
              "h-[calc(100dvh-58px-var(--messenger-banner-height,0px))] [body:has(.top-banner)_&]:h-[calc(100dvh-99px-var(--messenger-banner-height,0px))]",
            "z-nav shadow-xl",
          )}
          style={
            floating
              ? {
                  top: drawerTop,
                  bottom: 0,
                }
              : { top: drawerTop }
          }
        >
          <Dialog.Title className="sr-only">Menu</Dialog.Title>

          <nav
            className={cn(
              "flex flex-col gap-5 px-3 pt-5",
              // The floating drawer fills to the true viewport bottom (bottom:0
              // above), so its content clears the mobile toolbar by padding the
              // covered strip (100lvh-100dvh) plus the home-indicator safe area.
              floating
                ? "pb-[max(1.5rem,calc(100lvh-100dvh+env(safe-area-inset-bottom,0px)))]"
                : "pb-[max(1.5rem,env(safe-area-inset-bottom,1.5rem))]",
            )}
          >
            {/* PRIMARY NAVIGATION */}
            <ul className="border-nav-border/30 flex flex-col border-b">
              {mobileMenuFixtureData.primary.map((item, index) => (
                <li
                  key={item.type === "custom" ? `custom-${index}` : item.label}
                  className="border-nav-border/30 border-t"
                >
                  <DialogNavItem
                    item={item}
                    openItems={openItems}
                    onOpenItemChange={setOpenItems}
                  />
                </li>
              ))}
            </ul>

            <ul>
              {mobileMenuFixtureData.secondary.map((item, index) => (
                <li key={index} data-testid="nav-cta">
                  <DialogNavItem item={item} className="block px-2 py-1.5" />
                </li>
              ))}
              {drawerCrossSite && (
                <li className="sm:hidden" data-testid="nav-cross-site">
                  <DialogNavItem
                    item={drawerCrossSite}
                    className="block px-2 py-1.5"
                  />
                </li>
              )}
            </ul>

            <ul className="flex flex-col gap-4">
              {mobileMenuFixtureData.tertiary?.map((item, index) => (
                <li key={index}>
                  <DialogNavItem item={item} />
                </li>
              ))}
            </ul>
          </nav>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
